import React from "react";
import "../index.css";

export default function Help() {
  return (
    <div className="w-full h-full">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6">
        <header className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">MyTrailer Help Center</h1>
          <p className="text-gray-600 mt-2">Quick guides for getting the most out of MyTrailer.</p>
        </header>

        {/* Quick Links */}
        <nav className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {[
            ["Contact Us", "#contact-us"],
            ["Quick Start", "#quick-start"],
            ["Entries", "#entries"],
            ["Results", "#results"],
            ["Horses", "#horses"],
            ["Feed Plans", "#feed-plans"],
            ["Supplies Management", "#supplies-management"],
            ["Veterinarians & Farriers", "#veterinarians-farriers"],
            ["Family Management", "#family-management"],
            ["Associations", "#associations"],
            ["Calendar & Events", "#calendar-events"],
            ["Media Management", "#media-management"],
            ["Live Streaming", "#live-streaming"],
            ["Connect", "#connect-feature"],
            ["Widgets & Dashboard", "#widgets-dashboard"],
            ["Insights", "#saved-reports"],
            ["Analytics", "#analytics"],
            ["Score Simulator", "#score-simulator"],
            ["KCIRodeo Setup Wizard", "#setup-wizard"],
            ["Subscriptions", "#subscriptions-upgrades"],
            ["Notifications", "#notifications"],
            ["Troubleshooting", "#troubleshooting"],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="block px-4 py-3 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-blue-300 hover:bg-blue-50 text-sm font-medium text-gray-800 transition-colors transition-shadow duration-200"
            >
              {label}
            </a>
          ))}
        </nav>

        <section id="contact-us" className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Have a question or need help?</h2>
          <div className="space-y-4 text-gray-700">
 
            <div>
              <h3 className="font-semibold">Email Us</h3>
              <ul className="list-disc list-inside">
                <li>support@kcisoftware.net</li>
              </ul>
            </div>

          </div>
        </section>

        {/* Quick Start */}
        <section id="quick-start" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Quick Start</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">For KCIRodeo Users</h3>
              <ol className="list-decimal list-inside space-y-1 text-gray-800">
                <li>Go to Settings → KCIRodeo Setup Wizard to get guided through the entire setup process.</li>
                <li>The wizard will help you add associations, import entries, results, and memberships.</li>
                <li>Add your first horse.</li>
                <li>Review your imported data and analytics.</li>
              </ol>
              <p className="text-gray-600 mt-2">The setup wizard is perfect for first-time users and will import all your existing KCIRodeo data automatically.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">For New Users (Non-KCIRodeo)</h3>
              <ol className="list-decimal list-inside space-y-1 text-gray-800">
                <li>Add your first horse.</li>
                <li>Create an entry (manual entry or online form).</li>
                <li>Add results manually or through online forms.</li>
                <li>Review your results and analytics.</li>
              </ol>
              <p className="text-gray-600 mt-2">Tip: Use Settings anytime to adjust preferences and add KCIRodeo integration later.</p>
            </div>
          </div>
        </section>

        {/* Entries */}
        <section id="entries" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Entries</h2>
          <p className="text-gray-700 mb-3">Manage entries three ways: Manual, Import, and Online Entry (embedded form).</p>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Manual Entry</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Entries → Add New Entry.</li>
                <li>Fill in show details, events, fees, and contestant; save.</li>
              </ul>
              <p className="text-gray-600 mt-2">Use manual entries when you don’t have an online confirmation or for historical records.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Import Entries (KCIRodeo)</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Entries → Import Entries → select association and a show.</li>
                <li>Fetch, review, and save to your account.</li>
                <li>Calendar events can be created for imported entries (if enabled).</li>
              </ul>
              <p className="text-gray-600 mt-2">If fetching fails, verify Association Key, Member Key, and Member Password in Settings.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Add New Online Entry (Embedded Form)</h3>
              <ol className="list-decimal list-inside text-gray-700 space-y-1">
                <li>Entries → Add New Online Entry.</li>
                <li>Select the association and show.</li>
                <li>Complete the entry in the embedded form.</li>
                <li>After confirmation, MyTrailer retrieves it automatically.</li>
              </ol>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>No association listed? Add it under Settings.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Results */}
        <section id="results" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Results</h2>
          <p className="text-gray-700 mb-3">Track your competition results from shows and events with detailed performance data.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Importing Results from KCIRodeo</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Results → Import Results from KCIRodeo.</li>
                <li>Select your association and choose a show to import.</li>
                <li>Review the imported results before saving to your account.</li>
                <li>Results include event details, times, positions, earnings, and points.</li>
              </ul>
              <p className="text-gray-600 mt-2">Requires association credentials configured in Settings.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Manually Adding Results</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Results → Add New Result.</li>
                <li>Enter show details, event information, and performance data.</li>
                <li>Include final times, positions, earnings, and points earned.</li>
                <li>Link results to specific horses and family members.</li>
              </ul>
              <p className="text-gray-600 mt-2">Use manual entry for shows not available through KCIRodeo import.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Viewing & Managing Results</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>View results with filters and sorting options.</li>
                <li>Use date range and search to find specific shows.</li>
                <li>Filter by horse, event type, or performance criteria.</li>
                <li>Edit or delete results as needed.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">In Analytics</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Generate performance reports and analytics.</li>
                <li>Track earnings, points, and improvement over time.</li>
                <li>Export result data for external analysis.</li>
                <li>Some plans support advanced reporting features.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Horses */}
        <section id="horses" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Horses</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>Add and manage horses from the Horses page.</li>
            <li>Click the expand/collapse buttons or the horse name to expand details (no navigation).</li>
            <li>Categories: Farrier, Dental Care, Deworming, Vaccinations, Medical History, Breedings.</li>
            <li>Upload photos (mobile camera supported); images are optimized automatically.</li>
          </ul>
        </section>

        {/* Feed Plans */}
        <section id="feed-plans" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Feed Plans</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>Define feed products (hay, pellets, supplements).</li>
            <li>Create a Feed Plan with amounts, frequency, and instructions.</li>
            <li>Update plans over time and keep a history of changes.</li>
          </ul>
        </section>

        {/* Supplies Management */}
        <section id="supplies-management" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Supplies Management</h2>
          <p className="text-gray-700 mb-3">Track your horse care supplies, orders, and costs with detailed vendor management.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Creating Supply Orders</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Supplies → Add New Order.</li>
                <li>Enter vendor information, order date, and shipping costs.</li>
                <li>Add multiple line items with product details, quantities, and prices.</li>
                <li>Include tax rates and other fees as needed.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Managing Vendors & Products</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Vendor names are auto-suggested from previous orders.</li>
                <li>Brand and category lookups help organize your supplies.</li>
                <li>Track unit descriptions and pricing for cost analysis.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Order Tracking & Reports</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Filter orders by date range and vendor.</li>
                <li>View detailed order breakdowns with expandable sections.</li>
                <li>Generate cost reports for budgeting and analysis.</li>
                <li>Export order data for external accounting systems.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Veterinarians & Farriers */}
        <section id="veterinarians-farriers" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Veterinarians & Farriers</h2>
          <p className="text-gray-700 mb-3">Manage your horse care professionals with detailed contact information and service tracking.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Adding Veterinarians</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Veterinarians → Add New Veterinarian.</li>
                <li>Enter name, clinic, phone, email, and address.</li>
                <li>Add specialties and notes for reference.</li>
                <li>Set as primary veterinarian for quick access.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Managing Farriers</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Similar process to veterinarians with farrier-specific fields.</li>
                <li>Track service history and scheduling.</li>
                <li>Link farrier services to specific horses.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Widget Integration</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Add Primary Veterinarian and Primary Farrier widgets to your dashboard.</li>
                <li>Quick access to contact information and service history.</li>
                <li>Configure widget display options (clinic, email visibility).</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Family Management */}
        <section id="family-management" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Family Management</h2>
          <p className="text-gray-700 mb-3">Manage multiple family members and their individual horse records, entries, and results.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Adding Family Members</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Settings → Family Management tab.</li>
                <li>Click "Add Family Member"</li>
                <li>Enter name and relationship details.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Managing Member Data</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Assign horses, entries, and results to specific family members.</li>
                <li>Switch context when importing entries or adding new records.</li>
                <li>View individual member analytics and performance data.</li>
                <li>Set member-specific association credentials.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Subscription Limits</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Family plans support multiple members (check your subscription limits).</li>
                <li>Each member can have their own horses and entries.</li>
                <li>Shared access to family resources and reports.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Associations */}
        <section id="associations" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Associations</h2>
          <p className="text-gray-700 mb-3">Associations are used for online entries and imports.  If your association uses KCI for online entries and results, you 
            likely have a user account with that association. Contact your association to get your User Id and Password and enter it here.
            <br /><br />Once you have added your association, you can select it before 
            opening the online entry form.  Association Configuration will allow you to import your entries, results, memberships from your associations.
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>Select your association from the dropdown menu in Settings, add your user id and password.  You can also add multiple associations 
            if you have accounts with different associations.</li>
          </ul>
        </section>

        {/* Calendar & Events */}
        <section id="calendar-events" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Calendar & Events</h2>
          <p className="text-gray-700 mb-3">Comprehensive calendar management with event creation, mapping, and scheduling features.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Creating Events</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Calendar → Create Event to add new events manually.</li>
                <li>Set event name, date, time, duration, and description.</li>
                <li>Add location with address or coordinates for mapping.</li>
                <li>Choose event type (general, show, training, etc.).</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Automatic Event Creation</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Entries can create calendar events automatically.</li>
                <li>Events include show details (name, date, location).</li>
                <li>Existing events for the same confirmation number are updated (not duplicated).</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Calendar Views & Navigation</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Switch between Day, Week, and Month views.</li>
                <li>Navigate using arrow buttons or date picker.</li>
                <li>Click on events to view details or edit.</li>
                <li>Use the event map to see locations visually.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Location & Mapping</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Add precise locations using the location picker.</li>
                <li>View events on an interactive map.</li>
                <li>Get directions to event locations via Google Maps.</li>
                <li>Set coordinates manually for remote locations.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Media Management */}
        <section id="media-management" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Media Management</h2>
          <p className="text-gray-700 mb-3">Create and manage events with photo and video capture to preserve your memories.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Creating Media Events</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Media → Create Event to start a new media event.</li>
                <li>Enter event name, location, dates, and description.</li>
                <li>Events can span multiple days for longer competitions.</li>
                <li>Set event type and category for organization.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Uploading Media</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Upload photos and videos directly from your device.</li>
                <li>Mobile camera support for on-the-go capture.</li>
                <li>Images are automatically optimized for storage.</li>
                <li>Support for multiple file formats (JPG, PNG, MP4, etc.).</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Organizing & Viewing</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>View media in a gallery with thumbnail previews.</li>
                <li>Click to view full-size images and videos.</li>
                <li>Navigate between media items with arrow keys.</li>
                <li>Edit event details and manage media collections.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Subscription Requirements</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Media Management requires a Premium subscription.</li>
                <li>Upgrade to access photo and video features.</li>
                <li>Generous storage limits for your media collections.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Live Streaming */}
        <section id="live-streaming" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Live Streaming</h2>
          <p className="text-gray-700 mb-3">Broadcast live video streams and connect with viewers in real-time.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Starting a Stream</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Live Streaming → Start New Stream.</li>
                <li>Enter stream title and description.</li>
                <li>Allow camera and microphone permissions when prompted.</li>
                <li>Click "Start Streaming" to begin broadcasting.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Stream Management</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Toggle microphone on/off during streaming.</li>
                <li>Adjust stream quality settings (auto, high, medium, low).</li>
                <li>View connected viewers and manage access requests.</li>
                <li>End stream or start a new one as needed.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Viewer Access</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Viewers can request access to join your stream.</li>
                <li>Approve or deny viewer requests in real-time.</li>
                <li>Monitor viewer count and connection status.</li>
                <li>Share stream links with specific people.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Technical Requirements</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Requires HTTPS connection for camera access.</li>
                <li>Modern browser with WebRTC support.</li>
                <li>Stable internet connection for best quality.</li>
                <li>Premium subscription required for streaming features.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Connect Feature */}
        <section id="connect-feature" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Connect</h2>
          <p className="text-gray-700 mb-3">Connect with other MyTrailer users to share experiences, results, and build your network.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Finding Connections</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Search for other users by name, location, or association.</li>
                <li>Browse users who compete in similar events.</li>
                <li>Connect with friends and fellow competitors.</li>
                <li>View public profiles and competition history.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Managing Connections</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Send and receive connection requests.</li>
                <li>Accept or decline incoming requests.</li>
                <li>Manage your connection list and privacy settings.</li>
                <li>Block users if needed for privacy.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Sharing & Privacy</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Control what information is visible to connections.</li>
                <li>Share results and achievements with your network.</li>
                <li>Keep personal information private as desired.</li>
                <li>Connect with users from your associations.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Widgets & Dashboard */}
        <section id="widgets-dashboard" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Widgets & Dashboard</h2>
          <p className="text-gray-700 mb-3">Customize your dashboard with widgets to get quick access to important information.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Available Widgets</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Reminders:</strong> Upcoming calendar events and reminders</li>
                <li><strong>Recent Entries:</strong> Your most recent competition entries</li>
                <li><strong>Year to Date Stats:</strong> Performance metrics and earnings</li>
                <li><strong>Primary Veterinarian:</strong> Quick access to vet contact info</li>
                <li><strong>Primary Farrier:</strong> Quick access to farrier contact info</li>
                <li><strong>Memberships:</strong> Track active association memberships</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Adding & Managing Widgets</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Click "Add Widget" to see available options.</li>
                <li>Drag widgets to reorder them on your dashboard.</li>
                <li>Click the edit button to configure widget settings.</li>
                <li>Remove widgets you don't need.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Edit Mode</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Enter Edit Mode to rearrange and configure widgets.</li>
                <li>Drag the move handle to reorder widgets.</li>
                <li>Use the edit button to customize widget settings.</li>
                <li>Exit Edit Mode when you're done customizing.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Widget Configuration</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Customize titles and display options for each widget.</li>
                <li>Set date ranges for statistics widgets.</li>
                <li>Choose which information to show or hide.</li>
                <li>Configure refresh intervals for real-time data.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Saved Reports */}
        <section id="saved-reports" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Insights</h2>
          <p className="text-gray-700 mb-3">Create, save, and manage custom reports for detailed analysis of your performance and data.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Creating Reports</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Saved Reports → Create New Report.</li>
                <li>Choose from pre-built templates or create custom reports.</li>
                <li>Set filters for date ranges, events, horses, and more.</li>
                <li>Configure chart types and display options.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Report Types</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Performance Reports:</strong> Results analysis and trends</li>
                <li><strong>Entry Reports:</strong> Competition patterns and history</li>
                <li><strong>Horse Reports:</strong> Individual horse performance</li>
                <li><strong>Custom Reports:</strong> Build your own with specific criteria</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Auto-Update Features</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Enable auto-update to keep reports current with new data.</li>
                <li>Reports automatically refresh when new results are added.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Export Features</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Export reports to CSV format for external analysis.</li>
                <li>Download filtered report data with all current filters applied.</li>
                <li>CSV files include show dates, names, contestants, events, times, positions, earnings, and points.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Subscription Requirements</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Insights require a Showpremium subscription.</li>
                <li>Advanced analytics and custom reporting features.</li>
                <li>Unlimited report creation and storage.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Analytics */}
        <section id="analytics" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Analytics</h2>
          <p className="text-gray-700 mb-3">Comprehensive performance analytics and financial tracking for your horse activities.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Performance Analytics</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>View high-level performance metrics and trends.</li>
                <li>Track total points, earnings, and show participation.</li>
                <li>Analyze performance by horse, event type, and time period.</li>
                <li>Compare performance across different years.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Financial Tracking</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Track total earnings from competition results.</li>
                <li>Monitor expenses for horse care, supplies, and entries.</li>
                <li>Calculate net profit and return on investment.</li>
                <li>Break down costs by category and horse.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Filtering & Customization</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Filter by date range and other dimensions (plan dependent).</li>
                <li>View analytics for specific family members.</li>
                <li>Compare performance across different horses.</li>
                <li>Export analytics data for external analysis.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Yearly & Monthly Views</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Switch between different time periods for analysis.</li>
                <li>View monthly trends and seasonal patterns.</li>
                <li>Track year-over-year performance improvements.</li>
                <li>Identify peak performance periods.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Score Simulator */}
        <section id="score-simulator" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Score Simulator</h2>
          <p className="text-gray-700 mb-3">Practice and simulate competition scoring to improve your performance.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Using the Simulator</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Score Simulator to access the tool.</li>
                <li>Select event type and competition format.</li>
                <li>Enter practice times or scores.</li>
                <li>Compare against historical performance data.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Practice Scenarios</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Simulate different competition conditions.</li>
                <li>Practice with various scoring systems.</li>
                <li>Test strategies for different event types.</li>
                <li>Track improvement over time.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Performance Analysis</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Compare simulated scores with actual results.</li>
                <li>Identify areas for improvement.</li>
                <li>Set realistic performance goals.</li>
                <li>Track progress toward targets.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* KCIRodeo Setup Wizard */}
        <section id="setup-wizard" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">KCIRodeo Setup Wizard</h2>
          <p className="text-gray-700 mb-3">Get guided through the complete KCIRodeo integration setup process.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">What is the Setup Wizard?</h3>
              <p className="text-gray-700 mb-3">
                The KCIRodeo Setup Wizard is a guided process that helps first-time users set up their KCIRodeo integration 
                and import all their existing data in just a few steps.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">How to Access the Wizard</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Go to Settings → KCIRodeo Setup Wizard tab</li>
                <li>Click "Start Setup Wizard" to begin</li>
                <li>The wizard will open in a modal overlay</li>
                <li>Follow the step-by-step instructions</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Wizard Steps</h3>
              <ol className="list-decimal list-inside text-gray-700 space-y-1">
                <li><strong>Add KCIRodeo Associations:</strong> Configure your association credentials</li>
                <li><strong>Import Entries:</strong> Select and import your competition entries</li>
                <li><strong>Import Results:</strong> Select and import your competition results</li>
                <li><strong>Import Memberships:</strong> Import your association memberships</li>
                <li><strong>Setup Complete:</strong> Review what was imported and next steps</li>
              </ol>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Features</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Step-by-step guidance with progress indicators</li>
                <li>Automatic validation of association credentials</li>
                <li>Show selection for targeted imports</li>
                <li>Real-time import progress and results</li>
                <li>Skip options for optional steps</li>
                <li>Summary of imported data at completion</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">When to Use</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>First-time KCIRodeo users setting up their account</li>
                <li>Users who want to import all their historical data at once</li>
                <li>Anyone who prefers guided setup over manual configuration</li>
                <li>Users who want to ensure they don't miss any setup steps</li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">💡 Pro Tip</h4>
              <p className="text-sm text-blue-800">
                The setup wizard is a one-time process. Once you've completed it, you can use the regular 
                import features in Entries, Results, and Memberships pages for ongoing data management.
              </p>
            </div>
          </div>
        </section>

        {/* Subscriptions & Upgrades */}
        <section id="subscriptions-upgrades" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Subscriptions & Upgrades</h2>
          <p className="text-gray-700 mb-3">Choose the right plan for your needs and understand what features are available at each level.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Available Plans</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Horse Management:</strong> Basic horse care tracking, supplies, feed plans, health records</li>
                <li><strong>Show Individual:</strong> Horse management + entries, results, memberships, analytics</li>
                <li><strong>Show Family:</strong> Individual features + multiple family members, shared resources</li>
                <li><strong>Show Premium:</strong> Family features + live streaming, media management, advanced reports</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Feature Access by Plan</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Basic Features:</strong> Horse management, supplies, veterinarians, farriers</li>
                <li><strong>Show Features:</strong> Entries, results, associations, analytics, data export</li>
                <li><strong>Premium Features:</strong> Live streaming, media management, advanced reporting</li>
                <li><strong>Family Features:</strong> Multiple users, shared data, family analytics</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Upgrading & Billing</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Compare plans on the Subscription page.</li>
                <li>Upgrades apply immediately; features become available right away.</li>
                <li>Billing is handled securely via Stripe.</li>
                <li>Prorated billing for mid-cycle upgrades.</li>
                <li>Cancel or downgrade anytime (changes take effect at next billing cycle).</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Usage Limits</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Horse limits vary by plan (check your subscription details).</li>
                <li>Family member limits for family plans.</li>
                <li>Storage limits for media and reports.</li>
                <li>Export and reporting frequency limits.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Notifications */}
        <section id="notifications" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Notifications</h2>
          <p className="text-gray-700 mb-3">Stay informed about important updates, errors, and system messages.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">In-App Notifications</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Success and error notifications appear in-app.</li>
                <li>After online entry processing, you'll see an import summary.</li>
                <li>Notifications appear in the top-right corner of the screen.</li>
                <li>Click to dismiss or view more details.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Notification Types</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Success:</strong> Green notifications for completed actions</li>
                <li><strong>Error:</strong> Red notifications for failed operations</li>
                <li><strong>Warning:</strong> Yellow notifications for important alerts</li>
                <li><strong>Info:</strong> Blue notifications for general information</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Auto-Update Notifications</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Get notified when saved reports are automatically updated.</li>
                <li>See summary of new data added to your reports.</li>
                <li>Track changes and improvements in your analytics.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Managing Notifications</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Notifications auto-dismiss after a few seconds.</li>
                <li>Click the X to manually dismiss notifications.</li>
                <li>View notification history in the notification panel.</li>
                <li>Configure notification preferences in Settings.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Troubleshooting */}
        <section id="troubleshooting" className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Troubleshooting</h2>
          <p className="text-gray-700 mb-6">Common issues and their solutions to help you get back on track quickly.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Entry & Import Issues</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700">Online entry did not import</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Confirm your association credentials are correct in Settings.</li>
                    <li>Ensure the confirmation number is visible after entry completion.</li>
                    <li>Try refreshing the embedded form or use "Open in new tab."</li>
                    <li>Retry the import. If the issue persists, contact Support with the time and last 4 digits of the confirmation number.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">500 error or "Incorrect syntax near '>'"</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Retry after a few minutes - this is usually a temporary server issue.</li>
                    <li>Verify association selection and credentials are correct.</li>
                    <li>If it continues, contact Support with your time window and confirmation number last 4 digits.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">No associations available</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Add association credentials under Settings → Associations.</li>
                    <li>Ensure they are active and correct.</li>
                    <li>Contact your association to verify your account status.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Account & Subscription Issues</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700">Can't add horse due to limits</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Check your plan limits on the Subscription page.</li>
                    <li>Consider upgrading to a higher tier if you need more horses.</li>
                    <li>Remove unused horses if you're at your limit.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">Feature not available</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Check if the feature requires a specific subscription tier.</li>
                    <li>Verify your subscription is active and up to date.</li>
                    <li>Contact Support if you believe you should have access.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">Login problems</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Clear your browser cache and cookies.</li>
                    <li>Try logging in from an incognito/private browser window.</li>
                    <li>Use the password reset feature if you've forgotten your password.</li>
                    <li>Contact Support if login issues persist.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Technical Issues</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700">Slow loading or timeouts</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Check your internet connection speed.</li>
                    <li>Try refreshing the page or clearing browser cache.</li>
                    <li>Close other browser tabs to free up memory.</li>
                    <li>Try a different browser if problems persist.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">Camera/microphone not working (Live Streaming)</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Ensure you're using HTTPS (required for camera access).</li>
                    <li>Check browser permissions for camera and microphone.</li>
                    <li>Try a different browser or device.</li>
                    <li>Ensure no other applications are using the camera.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">File upload issues</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Check file size limits (usually 10MB for images, 100MB for videos).</li>
                    <li>Ensure file format is supported (JPG, PNG, MP4, etc.).</li>
                    <li>Try uploading one file at a time.</li>
                    <li>Check your internet connection stability.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Data & Sync Issues</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700">Data not syncing between devices</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Ensure you're logged into the same account on all devices.</li>
                    <li>Refresh the page or restart the app.</li>
                    <li>Check your internet connection.</li>
                    <li>Wait a few minutes for sync to complete.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">Missing or incorrect data</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Check if data was entered under a different family member.</li>
                    <li>Verify date ranges in filters and reports.</li>
                    <li>Check if data was accidentally deleted.</li>
                    <li>Contact Support for data recovery assistance.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Getting Additional Help</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Check this help section for detailed feature guides.</li>
                <li>Contact Support at support@kcisoftware.net with specific details.</li>
                <li>Include error messages, steps to reproduce, and your account information.</li>
                <li>For urgent issues, mention "URGENT" in your subject line.</li>
              </ul>
            </div>
          </div>
        </section>

        <footer className="text-sm text-gray-500">
          <p></p>
        </footer>
      </div>
    </div>
  );
}


